import { DictionaryValidators } from './../../core/validators/dictionnary.validators';
import { Observable } from 'rxjs';
import { CodeSet, Code, Dictionary } from './../../core/model/dictionary';
import { DictionaryService } from './../../core/service/dictionary.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { NGXLogger } from 'ngx-logger';
import { UntypedFormBuilder, Validators, ValidationErrors } from '@angular/forms';
import { Papa } from 'ngx-papaparse';
import { CsvError } from '../../core/model/error';
import { MessageHandlerService } from '../../core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { sortBy } from 'lodash';
import { saveAs } from '@progress/kendo-file-saver';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query';
import { GridComponent } from '@progress/kendo-angular-grid';
import { GridEventHandler } from 'src/app/core/model/grid-settings';

@Component({
  selector: 'app-codeset-edit',
  templateUrl: './codeset-edit.component.html',
  styleUrls: ['./codeset-edit.component.scss']
})
export class CodeSetEditComponent implements OnInit
{
  @ViewChild('grid') public grid: GridComponent;
  @ViewChild('csvFileInput') public csvFileInput: any;

  /**
   * Total number of code sets.
   */
  public codeSetCount = 0;

  /**
   * Kendo grid view for code set.
   */
  public codeSetGrid: GridViewManager;

  /**
   * Controls whether the advanced feature of the Kendo grid for code set is shown.
   */
  public codeSetGridAdvanced = false;

  /**
   * The selected code set.
   */
  public selectedCodeSet: CodeSet = null;

  /**
   * The selected code.
   */
  public selectedCode: Code = null;

  /**
   * Total number of codes for the selected code set.
   */
  public codeCount = 0;

  /**
   * Kendo grid view for code.
   */
  public codeGrid: GridViewManager;

  /**
   * Controls whether the advanced feather of the Kendo grid for code is shown.
   */
  public codeGridAdvanced = false;

  /**
   * FormGroup for in-grid editing of code.
   */
  public codeFormGroup: any = null;

  /**
   * FormGroup for in-grid editing of code set.
   */
  public codeSetFormGroup: any = null;

  /**
   * Controls the code set deletion confirmation dialog.
   */
  public codeSetDeleteDialogOpened = false;

  /**
   * Controls the code deletion confirmation dialog.
   */
  public codeDeleteDialogOpened = false;

  /**
   * Error message to display in the UI.
   */
  public errorMessage: string = null;

  /**
   * Errors when parsing the CSV file.
   */
  public csvErrors: CsvError[] = [];

  /**
   * Translation message when CodeSet deletion failed
   */
  public failedSaveCodesetError: string;

  /**
   * Translation message when Code deletion failed
   */
  public failedSaveCodeError: string;

  public filterText = '';

  public pageSizes = [5, 10, 25, 50, 100];

  public flagCategoryCodesetId: number = null;

  public codeSets = [];

  /**
   * Constructor
   */
  constructor(
    private dictionaryService: DictionaryService,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private formBuilder: UntypedFormBuilder,
    private papa: Papa,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private dictionaryValidators: DictionaryValidators,
    private searchHelperService: SearchHelperService)
  {
    const pageSize = this.appConfigService.getConfig().pageSize;
    this.codeSetGrid = new GridViewManager(pageSize);
    this.codeGrid = new GridViewManager(pageSize);
  }

  /**
   * Component initialization.
   */
  public ngOnInit(): void
  {
    this.translate.get(['CODESET-FK-ERROR', 'CODE-FK-ERROR']).subscribe({
      next: (value) =>
      {
        this.failedSaveCodesetError = value['CODESET-FK-ERROR'];
        this.failedSaveCodeError = value['CODE-FK-ERROR'];
      }
    });

    this.codeSetGrid.startLoading();

    // fetch code sets from server
    this.dictionaryService.getCodeSets().subscribe({
      next: (data) =>
      {
        this.codeSetGrid.setData(data);
        this.codeSetGrid.setSort([{ field: 'name', dir: 'asc' }]);
        this.refreshCodeSetCount();
        const flagCategory = data.filter(e => e.name === 'FlagCategory');
        this.flagCategoryCodesetId = flagCategory.length === 1 ? flagCategory[0].id : 0;
        this.codeSets = sortBy(data.filter(e => e.id !== this.flagCategoryCodesetId), ['name']);
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.codeSetGrid.stopLoading();
      }
    });
  }

  public codeSet(id: number): CodeSet
  {
    return this.codeSets.find((x) => x.id === id);
  }

  /**
   * Handles the row selection change of the Kendo grid for CodeSet.
   *
   * @param selections Kendo grid selected keys.
   */
  public onSelectedKeysChange(selections): void
  {
    if (selections.length > 0)
    {
      const id = selections[0]; // we only allow single selection
      this.selectedCodeSet = this.codeSetGrid.data.find(x => x.id === id);
      this.codeGrid.startLoading();
      this.dictionaryService.getCodesByCodeSetId(this.selectedCodeSet.id).subscribe({
        next: data =>
        {
          this.codeGrid.setData(data);
          this.codeGrid.setSort([{ field: 'displayName', dir: 'asc' }]);
          this.refreshCodeCount();
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        },
        complete: () =>
        {
          this.codeGrid.stopLoading();
        }
      });
    }
    else // if the row is unselected.
    {
      this.selectedCodeSet = null;
    }
  }

  /**
   * Handles the add new row event of the Kendo grid for Code.
   * Adds a new row to the grid, and creates a FormGroup for data input.
   */
  public codeAddHandler(event: GridEventHandler): void
  {
    this.codeFormGroup = this.formBuilder.group(
      {
        code: this.codeValidatorConfig(),
        codeSystem: this.codeSystemValidatorConfig(),
        sourceCodeSystem: ['', Validators.maxLength(50)],
        alternateCode: ['', Validators.maxLength(10000)],
        displayName: [{ value: '', disabled: false }, { validators: [Validators.required, Validators.maxLength(1000)] }],
        codeSetPerRecord: null,
        icon: [null, Validators.maxLength(50)],
        editing: true
      }
    );

    event.sender.addRow(this.codeFormGroup);
  }

  /**
   * Handles the add new row event of the Kendo grid for Code.
   * Adds a new row to the grid, and creates a FormGroup for data input.
   */
  public codeSetAddHandler(event: GridEventHandler): void
  {
    this.selectedCodeSet = null;
    this.codeSetFormGroup = this.formBuilder.group(
      {
        name: [
          { value: '', disabled: false },
          {
            validators: [Validators.required],
            asyncValidators: (): Observable<ValidationErrors | null> =>
              this.dictionaryValidators.validateCodeSetNameUnique(this.codeSetFormGroup.get('name'))
          }
        ],
        description: ['', Validators.required],
        editing: true
      }
    );

    event.sender.addRow(this.codeSetFormGroup);
  }

  public codeSetEditHandler(event: any): void
  {
    this.codeSetFormGroup = this.formBuilder.group(
      {
        id: event.dataItem.id,
        description: [event.dataItem.description, Validators.required],
        version: event.dataItem.version,
        editing: true
      }
    );

    event.sender.editRow(event.rowIndex, this.codeSetFormGroup);
  }

  /**
   * Handles the edit event of the code grid.
   */
  public codeEditHandler(event: GridEventHandler): void
  {
    this.codeFormGroup = this.formBuilder.group(
      {
        id: event.dataItem.id,
        codeSystem: this.codeSystemValidatorConfig(event.dataItem.code, event.dataItem.codeSystem),
        sourceCodeSystem: [event.dataItem.sourceCodeSystem, Validators.maxLength(50)],
        displayName: [{ value: event.dataItem.displayName, disabled: false }, { validators: [Validators.required, Validators.maxLength(1000)] }],
        code: this.codeValidatorConfig(event.dataItem.code, event.dataItem.codeSystem),
        alternateCode: [event.dataItem.alternateCode, Validators.maxLength(10000)],
        codeSetPerRecord: event.dataItem.codeSetPerRecord,
        icon: [event.dataItem.icon, Validators.maxLength(50)],
        version: event.dataItem.version,
        editing: true
      }
    );

    // Set values after setting the validator
    this.codeFormGroup.get('code').setValue(event.dataItem.code);
    this.codeFormGroup.get('codeSystem').setValue(event.dataItem.codeSystem);

    event.sender.editRow(event.rowIndex, this.codeFormGroup);
  }

  /**
   * Saves the edited code from the grid.
   *
   * @param event Save row edit event from the Kendo grid for Code.
   */
  public saveCode(event): void
  {
    if (event.isNew)
    {
      // constructs a new Code object
      const code =
      {
        code: event.dataItem.code,
        alternateCode: event.dataItem.alternateCode,
        displayName: event.dataItem.displayName,
        codeSystem: event.dataItem.codeSystem.toUpperCase(),
        sourceCodeSystem: event.dataItem.sourceCodeSystem.toUpperCase(),
        codeSetPerRecord: event.dataItem && event.dataItem.codeSetPerRecord && event.dataItem.codeSetPerRecord.id > 0 ?
          { id: event.dataItem.codeSetPerRecord.id } : null,
        icon: event.dataItem.icon,
        codeSet:
        {
          id: this.selectedCodeSet.id
        }
      };

      // call DictionaryService to create the new code record on the server.
      this.dictionaryService.createCode(code).subscribe({
        next: (data: Dictionary) =>
        {
          this.messageHandlerService.displaySuccess('CODE-SAVED-SUCCESSFULLY');

          // update the item with the server generated ID
          event.dataItem = data;

          // close the editing row, and update the grid.
          this.codeGrid.saveHandler(event);
          this.refreshCodeCount();
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    else
    {
      const code = this.codeFormGroup.value;
      code.codeSystem = this.codeFormGroup.value.codeSystem;
      code.code = this.codeFormGroup.value.code;
      code.alternateCode = this.codeFormGroup.value.alternateCode;
      code.codeSet = { id: this.selectedCodeSet.id };
      code.codeSetPerRecord = this.codeFormGroup.value.codeSetPerRecord && this.codeFormGroup.value.codeSetPerRecord.id > 0 ?
        { id: this.codeFormGroup.value.codeSetPerRecord.id } : null;

      delete code.editing;

      // update the code
      this.dictionaryService.updateCode(code).subscribe({
        next: (data: Dictionary) =>
        {
          this.messageHandlerService.displaySuccess('CODE-SAVED-SUCCESSFULLY');

          // close the editing row, and update the grid.
          event.dataItem = data;
          this.codeGrid.saveHandler(event);
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
  }

  /**
   * Saves the edited code set from the grid.
   *
   * @param event Save row edit event from the Kendo grid for CodeSet.
   */
  public saveCodeSet(event): void
  {
    if (event.isNew)
    {
      // constructs a new CodeSet
      const codeSet =
      {
        name: event.dataItem.name,
        description: event.dataItem.description
      };

      // call DictionaryService to create the new code set record on the server.
      this.dictionaryService.createCodeSet(codeSet).subscribe({
        next: (data) =>
        {
          this.messageHandlerService.displaySuccess('DICTIONARY-SAVED-SUCCESSFULLY');

          // update the item with the server generated ID
          event.dataItem.id = data.id;

          // close the editing row, and update the grid.
          this.codeSetGrid.saveHandler(event);
          this.refreshCodeSetCount();
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
    else
    {
      const codeSet = event.formGroup.value;
      codeSet.name = event.dataItem.name;

      delete codeSet.editing;

      // update the code set
      this.dictionaryService.updateCodeSet(codeSet).subscribe({
        next: (data) =>
        {
          this.messageHandlerService.displaySuccess('DICTIONARY-SAVED-SUCCESSFULLY');

          // close the editing row, and update the grid.
          event.dataItem = data;
          this.codeSetGrid.saveHandler(event);
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError(error);
        }
      });
    }
  }

  /**
   * Opens the delete code set confirmation dialog.
   *
   * @param codeSet The Code set to delete.
   */
  public openDeleteCodeSetDialog(codeSet: CodeSet): void
  {
    this.errorMessage = null;
    this.selectedCodeSet = codeSet;
    this.codeSetDeleteDialogOpened = true;
  }

  /**
   * Opens the delete code confirmation dialog.
   *
   * @param code The code to delete.
   */
  public openDeleteCodeDialog(code: Code): void
  {
    this.errorMessage = null;
    this.selectedCode = code;
    this.codeDeleteDialogOpened = true;
  }

  /**
   * Deletes a CodeSet.
   *
   * @param codeSetId ID of the code set to delete.
   */
  public deleteCodeSet(codeSet: CodeSet): void
  {
    this.dictionaryService.deleteCodeSet(codeSet.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('DICTIONARY-DELETED-SUCCESSFULLY');

        this.codeSetGrid.removeRow(codeSet);
        this.refreshCodeSetCount();
        this.codeSetDeleteDialogOpened = false;
        this.selectedCodeSet = null;
        this.collapseAllRows();
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.errorMessage = this.failedSaveCodesetError;
      }
    });
  }

  /**
   * Deletes a Code.
   *
   * @param code The code to delete.
   */
  public deleteCode(code: Code): void
  {
    this.dictionaryService.deleteCode(code.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('CODE-DELETED-SUCCESSFULLY');

        this.codeGrid.removeRow(code);
        this.refreshCodeCount();
        this.codeDeleteDialogOpened = false;
        this.selectedCode = null;
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.errorMessage = this.failedSaveCodeError;
      }
    });
  }

  /**
   * Process the code CSV file import.
   */
  public handleCsvInput(files: FileList): void
  {
    const csvFile = files.item(0); // only allow a single CSV file
    this.papa.parse(csvFile,
      {
        header: true,           // CSV file must have a header row
        delimiter: ',',         // values must be delimited by commas
        skipEmptyLines: true,   // Skip empty lines
        complete: result =>     // parsing file is async, and here is the call back function.
        {
          // create new codes
          const codes = result.data.map(
            x =>
            ({
              codeSystem: x.codeSystem,
              sourceCodeSystem: x.sourceCodeSystem,
              code: x.code,
              alternateCode: x.alternateCode,
              displayName: x.displayName,
              codeSetPerRecord: x.codeSetPerRecord ? this.codeSets.find(y => y.name === x.codeSetPerRecord) : null,
              icon: x.icon,
              codeSet:
              {
                id: this.selectedCodeSet.id
              }
            }));

          this.csvErrors = result.errors;
          this.csvFileInput.nativeElement.value = '';

          if (result.errors.length === 0) // no errors in the CSV file
          {
            this.dictionaryService.createCodes(codes).subscribe({
              next: (data) =>
              {
                this.codeGrid.addOrUpdateRows(data, (x, y) => x.id === y.id);
                this.codeGrid.setSort([{ field: 'displayName', dir: 'asc' }]);
                this.refreshCodeCount();
                if (data.length < codes.length)
                {
                  this.messageHandlerService.displayWarning('CODE-SYSTEM-EXISTS');
                }
              },
              error: () =>
              {
                this.messageHandlerService.displayError('CODES-SAVE-ERROR');
              }
            });
          }
          else
          {
            this.messageHandlerService.displayError('CSV-PARSING-ERRORS');
            this.logger.error(this.csvErrors);
          }
        },
        // IO error encountered when reading the CSV file
        error: error => this.logger.error(error)
      });
  }

  /**
   * Export the selected code set to a CSV file, and trigger a browser download.
   */
  public exportCodeSetToCsv(): void
  {
    // convert the data array of codes to CSV text.
    const data = this.codeGrid.data.map(c => ({
      codeSystem: c.codeSystem,
      sourceCodeSystem: c.sourceCodeSystem,
      code: c.code,
      alternateCode: c.alternateCode,
      displayName: c.displayName,
      codeSetPerRecord: c.codeSetPerRecord ? c.codeSetPerRecord.name : '',
      icon: c.icon
    }));
    const csvContent = this.papa.unparse(sortBy(data, ['codeSystem']));

    // set the default file name to the code set name.
    const csvFileName = `${this.selectedCodeSet.name}.csv`;

    // create a Blob object
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);
    saveAs(url, csvFileName);
  }

  public searchFieldChange(filterText: string): void
  {
    this.codeSetGrid.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.codeSetGrid.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.codeSetGrid.state.filter = filter;
    this.codeSetGrid.gridData.data = filterBy(this.codeSetGrid.data, filter);
    this.codeSetCount = this.codeSetGrid.gridData.data.length;
    this.codeSetGrid.refreshView();
  }

  /**
   * Indicates if the grid details visibility. Details will show only for specific codesets.
   */
  public isShowMultipleCodeLanguages(): boolean
  {
    // Multilingual dictionary support is enabled just where it being used.
    // return (this.selectedCodeSet !== undefined && this.selectedCodeSet.name.toUpperCase() === 'COMMUNITYSERVICETYPE');
    return false;   // Hide that functionnality for now but keep it in the code for future usage (if needed)
  }

  /**
   * Collapse all code language grid row.
   */
  private collapseAllRows()
  {
    if (this.grid === undefined)
    {
      return;
    }
    // Will collapse all preceeding task details based on the page size and current page.
    const maxLength = this.appConfigService.getConfig().pageSize + this.grid.skip;
    for (let i = 0; i < maxLength; i++)
    {
      this.grid.collapseRow(i);
    }
  }
  /**
   * Refresh CodeSet Grid Count
   */
  private refreshCodeSetCount(): void
  {
    this.codeSetCount = this.codeSetGrid.data.length;
  }

  /**
   * Refresh Codes Grid Count
   */
  private refreshCodeCount(): void
  {
    this.codeCount = this.codeGrid.data.length;
  }

  private codeValidatorConfig(previousCode: string = '', previousCodeSystem: string = ''): any[]
  {
    return [
      { value: '', disabled: false },
      {
        validators: [Validators.required, Validators.maxLength(128)],
        asyncValidators: (): Observable<ValidationErrors | null> =>
          this.dictionaryValidators.validateCodeUnique(
            this.selectedCodeSet.id,
            this.codeFormGroup.get('code'),
            this.codeFormGroup.get('codeSystem'),
            previousCode,
            previousCodeSystem),
        updateOn: 'change'
      }
    ];
  }

  private codeSystemValidatorConfig(previousCode: string = '', previousCodeSystem: string = ''): any[]
  {
    return [
      { value: '', disabled: false },
      {
        validators: [Validators.required, Validators.maxLength(50)],
        asyncValidators: (): Observable<ValidationErrors | null> =>
          this.dictionaryValidators.validateCodeUnique(
            this.selectedCodeSet.id,
            this.codeFormGroup.get('code'),
            this.codeFormGroup.get('codeSystem'),
            previousCode,
            previousCodeSystem),
        updateOn: 'change'
      }
    ];
  }
}
